/**
 * Iter 152B — Agency Growth Cockpit
 *
 * Route: /app/growth-cockpit
 *
 * A single-page dashboard for whoever runs the agency itself. Combines
 * prospect funnel, leads pipeline value, agency campaigns (scope=agency),
 * agency outreach (scope=agency), internal marketing channels, upcoming
 * marketable days, agency-wide content heatmap, featured trends, brand
 * roster and team-activity (task throughput + top contributors).
 *
 * Uses one aggregate endpoint (`/workspaces/{ws}/growth-cockpit`) for speed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LayoutDashboard, CalendarDays, TrendingUp, Layers, Send, Plus, Trash2, Edit3, ExternalLink, Instagram, Facebook, Linkedin, Twitter, Youtube, Mail, Globe, Rocket, PhoneCall, Sparkles, MessageCircle, Target, DollarSign, Users, Activity, Award } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const PLATFORM_ICONS = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  twitter: Twitter, youtube: Youtube, email: Mail, whatsapp: MessageCircle, other: Globe,
};
const CAMPAIGN_STATUS_TONE = { draft: "chip", live: "chip-approved", paused: "chip-warning", done: "chip" };
const OUTREACH_STATUS_TONE = {
  to_reach: "chip", contacted: "chip-warning", responded: "chip-approved",
  declined: "chip-rejected", won: "chip-approved",
};
const PROSPECT_STAGES = ["new", "contacted", "qualified", "promoted"];
const LEAD_STAGES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

export default function GrowthCockpit() {
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms(activeWorkspaceId);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [campaignEdit, setCampaignEdit] = useState(null);
  const [outreachEdit, setOutreachEdit] = useState(null);
  const [channelsEdit, setChannelsEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const canManage = can("marketing.manage") || can("prospect.manage");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setErr(null);
    try {
      const { data: d } = await api.get(`/workspaces/${activeWorkspaceId}/growth-cockpit`);
      setData(d);
    } catch (e) {
      setErr(apiErrorMessage(e, "Couldn't load growth cockpit"));
    }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      if (confirmDel.kind === "campaign") await api.delete(`/campaigns/${confirmDel.id}`);
      if (confirmDel.kind === "outreach") await api.delete(`/outreach-targets/${confirmDel.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setConfirmDel(null); }
  };

  if (err) return <div className="p-6 text-sm text-destructive">{err}</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading growth cockpit…</div>;

  return (
    <>
      <PageHeader
        breadcrumb="Agency"
        title={`${data.workspace.name || "Agency"} Growth Cockpit`}
        description="Prospect funnel · pipeline value · agency campaigns · outreach · internal social growth · team throughput"
        testid="growth-cockpit-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => nav("/app/prospecting")} data-testid="growth-cockpit-prospecting">
              Prospecting →
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => nav("/app/leads")} data-testid="growth-cockpit-leads">
              Leads →
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-6" data-testid="growth-cockpit">
        {/* Top KPI strip */}
        <KpiStrip data={data} />

        {/* Prospect funnel + Leads pipeline (side-by-side) */}
        <div className="grid gap-4 lg:grid-cols-2">
          <ProspectFunnelSection funnel={data.prospect_funnel} />
          <LeadPipelineSection pipeline={data.lead_pipeline} />
        </div>

        {/* Internal marketing channels */}
        <ChannelsSection
          workspace={data.workspace}
          canEdit={canManage}
          onEdit={() => setChannelsEdit(true)}
        />

        {/* Team activity + Upcoming days */}
        <div className="grid gap-4 lg:grid-cols-2">
          <TeamActivitySection team={data.team_activity} />
          <UpcomingDaysSection days={data.upcoming_days} />
        </div>

        {/* Heatmap + Featured trends */}
        <div className="grid gap-4 lg:grid-cols-2">
          <HeatmapSection heatmap={data.heatmap} wsId={activeWorkspaceId} onChanged={load} />
          <FeaturedTrendsSection trends={data.featured_trends} />
        </div>

        {/* Brand roster */}
        <BrandRosterSection brands={data.brands} />

        {/* Agency campaigns */}
        <CampaignsSection
          campaigns={data.campaigns}
          canEdit={canManage}
          onNew={() => setCampaignEdit({ scope: "agency", status: "draft" })}
          onEdit={(c) => setCampaignEdit(c)}
          onDelete={(c) => setConfirmDel({ kind: "campaign", id: c.id, name: c.name })}
        />

        {/* Agency outreach */}
        <OutreachSection
          items={data.outreach}
          canEdit={canManage}
          onNew={() => setOutreachEdit({ scope: "agency", status: "to_reach", channel: "email" })}
          onEdit={(o) => setOutreachEdit(o)}
          onDelete={(o) => setConfirmDel({ kind: "outreach", id: o.id, name: o.name })}
        />
      </div>

      {campaignEdit && (
        <CampaignEditor
          wsId={activeWorkspaceId}
          initial={campaignEdit}
          onClose={() => setCampaignEdit(null)}
          onSaved={() => { setCampaignEdit(null); load(); }}
        />
      )}
      {outreachEdit && (
        <OutreachEditor
          wsId={activeWorkspaceId}
          initial={outreachEdit}
          onClose={() => setOutreachEdit(null)}
          onSaved={() => { setOutreachEdit(null); load(); }}
        />
      )}
      {channelsEdit && (
        <ChannelsEditor
          wsId={activeWorkspaceId}
          workspace={data.workspace}
          onClose={() => setChannelsEdit(false)}
          onSaved={() => { setChannelsEdit(false); load(); }}
        />
      )}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="growth-cockpit-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>&ldquo;{confirmDel?.name}&rdquo; will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─────────── Sections ───────────

function KpiStrip({ data }) {
  const prospects = data.prospect_funnel?.total || 0;
  const openPipe = data.lead_pipeline?.open_value || 0;
  const wonPipe = data.lead_pipeline?.won_value || 0;
  const liveCampaigns = (data.campaigns || []).filter((c) => c.status === "live").length;
  const wonOutreach = (data.outreach || []).filter((o) => o.status === "won").length;
  const teamDone = data.team_activity?.counts?.completed || 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="growth-cockpit-kpi-strip">
      <Kpi label="Prospects" value={prospects} icon={Target} tone="text-blue-600" testid="kpi-prospects" />
      <Kpi label="Pipeline (open)" value={fmtINR(openPipe)} icon={DollarSign} tone="text-amber-600" testid="kpi-pipeline-open" />
      <Kpi label="Won this year" value={fmtINR(wonPipe)} icon={Award} tone="text-emerald-600" testid="kpi-pipeline-won" />
      <Kpi label="Live campaigns" value={liveCampaigns} icon={Rocket} tone="text-primary" testid="kpi-live-campaigns" />
      <Kpi label="Partners won" value={wonOutreach} icon={PhoneCall} tone="text-emerald-600" testid="kpi-partners-won" />
      <Kpi label="Tasks done (14d)" value={teamDone} icon={Activity} tone="text-primary" testid="kpi-tasks-done" />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className={`w-3 h-3 ${tone}`} /> {label}
      </div>
      <div className={`text-xl font-mono font-semibold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function ProspectFunnelSection({ funnel }) {
  const stages = funnel?.stages || {};
  const total = funnel?.total || 0;
  const max = Math.max(1, ...PROSPECT_STAGES.map((s) => stages[s] || 0));
  return (
    <section className="border border-border" data-testid="growth-cockpit-funnel">
      <SectionHead icon={Target} title="Prospect funnel" count={total} sub="raw pipeline before qualification" />
      <div className="p-3 space-y-2">
        {PROSPECT_STAGES.map((s) => {
          const n = stages[s] || 0;
          const pct = (n / max) * 100;
          return (
            <div key={s} className="text-xs" data-testid={`funnel-stage-${s}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="uppercase tracking-widest text-[10px]">{s}</span>
                <span className="font-mono text-primary">{n}</span>
              </div>
              <div className="h-2 bg-muted">
                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LeadPipelineSection({ pipeline }) {
  const stages = pipeline?.stages || {};
  return (
    <section className="border border-border" data-testid="growth-cockpit-pipeline">
      <SectionHead icon={DollarSign} title="Sales pipeline" sub={`open ${fmtINR(pipeline?.open_value)} · won ${fmtINR(pipeline?.won_value)}`} />
      <div className="p-3 space-y-1.5">
        {LEAD_STAGES.map((s) => {
          const stage = stages[s] || { count: 0, value: 0 };
          const isWon = s === "won", isLost = s === "lost";
          const bar = isWon ? "bg-emerald-500" : isLost ? "bg-red-500" : "bg-amber-500";
          const totalValue = LEAD_STAGES.reduce((acc, k) => acc + ((stages[k] || {}).value || 0), 1);
          const pct = ((stage.value || 0) / totalValue) * 100;
          return (
            <div key={s} className="text-xs" data-testid={`pipeline-stage-${s}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="uppercase tracking-widest text-[10px]">{s}</span>
                <span className="font-mono text-primary">{stage.count} · {fmtINR(stage.value)}</span>
              </div>
              <div className="h-2 bg-muted">
                <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ChannelsSection({ workspace, canEdit, onEdit }) {
  const channels = workspace.marketing_channels || [];
  return (
    <section className="border border-border" data-testid="growth-cockpit-channels">
      <SectionHead
        icon={Globe} title="Agency social channels"
        count={channels.length}
        sub="track your own IG/LinkedIn/YouTube growth — manual entry until OAuth ships"
        action={canEdit && <Button size="sm" variant="outline" className="rounded-none h-7" onClick={onEdit} data-testid="growth-cockpit-channels-edit"><Edit3 className="w-3 h-3 mr-1" /> Edit</Button>}
      />
      {channels.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">
          No channels configured yet — {canEdit ? "click Edit to add your agency's IG, LinkedIn, YouTube handles." : "ask an admin to set them up."}
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4 p-3">
          {channels.map((c, i) => {
            const Icon = PLATFORM_ICONS[c.platform] || Globe;
            return (
              <div key={i} className="border border-border p-2 flex items-center gap-2" data-testid={`growth-cockpit-channel-${c.platform}`}>
                <Icon className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{c.handle || c.platform}</div>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground underline truncate block">
                      {c.url.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-mono">{typeof c.followers_manual === "number" ? c.followers_manual.toLocaleString() : "—"}</div>
                  <div className="text-[9px] text-muted-foreground">followers</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TeamActivitySection({ team }) {
  const counts = team?.counts || {};
  const top = team?.top_contributors || [];
  return (
    <section className="border border-border" data-testid="growth-cockpit-team">
      <SectionHead icon={Activity} title="Team activity" sub={`last ${team?.window_days || 14} days`} />
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-4 gap-1.5">
          <MiniStat label="Created" value={counts.created || 0} tone="text-primary" testid="team-created" />
          <MiniStat label="In progress" value={counts.in_progress || 0} tone="text-amber-600" testid="team-inprogress" />
          <MiniStat label="Completed" value={counts.completed || 0} tone="text-emerald-600" testid="team-completed" />
          <MiniStat label="Overdue" value={counts.overdue || 0} tone="text-red-600" testid="team-overdue" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Top contributors</div>
          {top.length === 0 ? (
            <div className="text-[11px] italic text-muted-foreground">No completed tasks in the window yet.</div>
          ) : (
            <ul className="space-y-1">
              {top.slice(0, 6).map((t) => (
                <li key={t.user_id} className="flex items-center gap-2 text-xs" data-testid={`team-contributor-${t.user_id}`}>
                  <Users className="w-3 h-3 text-primary shrink-0" />
                  <span className="truncate flex-1">{t.name}</span>
                  <span className="font-mono text-primary">{t.done}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, tone, testid }) {
  return (
    <div className="border border-border p-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-mono font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

function UpcomingDaysSection({ days }) {
  return (
    <section className="border border-border" data-testid="growth-cockpit-days">
      <SectionHead icon={CalendarDays} title="Upcoming marketable days" count={days.length} sub="agency-wide (all industries)" />
      {days.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">Nothing on the horizon.</div>
      ) : (
        <ul className="divide-y divide-border max-h-72 overflow-y-auto">
          {days.slice(0, 12).map((d, i) => (
            <li key={`${d.id}-${d.date}-${i}`} className="p-2 flex items-center gap-2 text-xs">
              <span className="w-16 font-mono text-primary shrink-0">{d.date?.slice(5)}</span>
              <span className="text-lg leading-none">{d.emoji || "📅"}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                {d.description && <div className="text-[10px] text-muted-foreground truncate">{d.description}</div>}
              </div>
              {d.category && <span className="chip text-[10px]">{d.category}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FeaturedTrendsSection({ trends }) {
  return (
    <section className="border border-border" data-testid="growth-cockpit-trends">
      <SectionHead icon={Sparkles} title="Featured trends" count={trends.length} sub="workspace-wide daily curation" />
      {trends.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">No featured trends yet — RSS auto-ingest runs nightly.</div>
      ) : (
        <ul className="divide-y divide-border max-h-72 overflow-y-auto">
          {trends.slice(0, 8).map((t) => {
            const angle = t.content_angle || {};
            return (
              <li key={t.id} className="p-2 text-xs" data-testid={`growth-cockpit-trend-${t.id}`}>
                <div className="font-semibold truncate">{angle.headline || t.title}</div>
                {angle.hook && <div className="text-[10px] text-muted-foreground italic line-clamp-1">{angle.hook}</div>}
                {t.url && (
                  <a href={t.url} target="_blank" rel="noreferrer" className="text-[10px] underline text-muted-foreground">
                    <ExternalLink className="w-2.5 h-2.5 inline mr-0.5" /> Source
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function HeatmapSection({ heatmap, wsId, onChanged }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const cells = useMemo(() => {
    const start = new Date(heatmap.window_start);
    const end = new Date(heatmap.window_end);
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      const iso = cur.toISOString().slice(0, 10);
      out.push({ date: iso, count: heatmap.counts[iso] || 0 });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [heatmap]);
  const maxCount = Math.max(1, ...cells.map((c) => c.count));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <section className="border border-border" data-testid="growth-cockpit-heatmap">
      <SectionHead icon={LayoutDashboard} title="Agency-wide content load" sub="all brands · red = >5 posts/day" />
      <div className="p-3">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
          {cells.map((c) => {
            const intensity = c.count === 0 ? 0 : Math.min(1, c.count / maxCount);
            const isToday = c.date === today;
            const overload = c.count > 5;
            const bg = c.count === 0 ? "hsl(var(--muted))" : overload ? `rgba(239,68,68,${0.4 + 0.5 * intensity})` : `rgba(16,185,129,${0.2 + 0.6 * intensity})`;
            return (
              <button
                key={c.date}
                type="button"
                onClick={() => c.count > 0 && setSelectedDate(c.date)}
                className={`aspect-square text-[8px] flex items-center justify-center font-mono ${c.count > 0 ? "hover:ring-1 hover:ring-primary cursor-pointer" : "cursor-default"}`}
                style={{ background: bg, outline: isToday ? "1.5px solid hsl(var(--primary))" : "none" }}
                title={`${c.date} — ${c.count} post${c.count === 1 ? "" : "s"}${overload ? " · OVERLOAD" : ""}`}
                data-testid={`growth-heatmap-cell-${c.date}`}>
                {c.count > 0 && c.count}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{heatmap.window_start}</span>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "rgba(16,185,129,0.5)" }} /> normal</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2" style={{ background: "rgba(239,68,68,0.6)" }} /> overload (&gt;5)</span>
          </div>
          <span>{heatmap.window_end}</span>
        </div>
      </div>
      {selectedDate && (
        <HeatmapDayDrawer
          wsId={wsId} date={selectedDate}
          onClose={() => setSelectedDate(null)}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

function HeatmapDayDrawer({ wsId, date, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/workspaces/${wsId}/growth-cockpit/day-load?date=${date}`);
      setData(d);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't load day"));
    }
  }, [wsId, date]);
  useEffect(() => { load(); }, [load]);

  const reschedule = async (item, offsetDays) => {
    setBusy(item.id);
    try {
      const d = new Date(date); d.setDate(d.getDate() + offsetDays);
      const iso = d.toISOString().slice(0, 10);
      await api.patch(`/content-items/${item.id}/reschedule`, { planned_date: iso });
      toast.success(`Moved to ${iso}`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reschedule failed"));
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-none" data-testid="growth-heatmap-day-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{date}</span>
            {data?.overload && <span className="chip chip-rejected text-[10px]">Overloaded ({data.count} posts)</span>}
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground">Loading…</div> : data.items.length === 0 ? (
          <div className="text-xs italic text-muted-foreground">No posts scheduled.</div>
        ) : (
          <ul className="divide-y divide-border max-h-96 overflow-y-auto" data-testid="growth-heatmap-day-list">
            {data.items.map((it) => (
              <li key={it.id} className="py-2 text-xs" data-testid={`growth-heatmap-item-${it.id}`}>
                <div className="flex items-center gap-2">
                  <span className="chip text-[10px]">{it.brand_name}</span>
                  <span className="font-medium truncate flex-1">{it.name || it.topic}</span>
                </div>
                <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                  {it.channel || it.category}
                  {it.status && ` · ${it.status}`}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, -1)}>
                    ← -1d
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, 1)}>
                    +1d →
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none h-6 px-1.5 text-[10px]"
                          disabled={busy === it.id} onClick={() => reschedule(it, 7)}>
                    +1w
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrandRosterSection({ brands }) {
  const nav = useNavigate();
  return (
    <section className="border border-border" data-testid="growth-cockpit-brands">
      <SectionHead icon={Layers} title="Brand portfolio" count={brands.length} sub="jump into a brand's own cockpit" />
      {brands.length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">No brands yet — add your first from CRM → Brands.</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4 p-3">
          {brands.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => nav(`/app/brands/${b.id}/cockpit`)}
              className="border border-border p-2 text-left hover:ring-1 hover:ring-primary flex items-center gap-2"
              data-testid={`growth-cockpit-brand-${b.id}`}
            >
              <div className="w-8 h-8 bg-muted flex items-center justify-center shrink-0">
                {b.logo_url
                  ? <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover" />
                  : <Layers className="w-3.5 h-3.5 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{b.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">{b.industry || "—"}</div>
              </div>
              <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function CampaignsSection({ campaigns, canEdit, onNew, onEdit, onDelete }) {
  const buckets = useMemo(() => {
    const b = { draft: [], live: [], paused: [], done: [] };
    (campaigns || []).forEach((c) => { (b[c.status] || b.draft).push(c); });
    return b;
  }, [campaigns]);
  return (
    <section className="border border-border" data-testid="growth-cockpit-campaigns">
      <SectionHead
        icon={Rocket} title="Agency campaigns" count={(campaigns || []).length}
        sub="our own growth pushes — hiring, product launches, PR moments"
        action={canEdit && <Button size="sm" className="rounded-none h-7" onClick={onNew} data-testid="growth-cockpit-campaign-new"><Plus className="w-3 h-3 mr-1" /> New campaign</Button>}
      />
      {(campaigns || []).length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">No agency campaigns yet — plan a hiring drive, a launch, or a PR push.</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-4 p-3">
          {["draft", "live", "paused", "done"].map((s) => (
            <div key={s} className="border border-border" data-testid={`growth-campaigns-${s}`}>
              <div className={`px-2 py-1.5 border-b border-border text-[10px] uppercase tracking-widest font-semibold ${CAMPAIGN_STATUS_TONE[s]}`}>{s} · {buckets[s].length}</div>
              <div className="p-2 space-y-1.5 max-h-60 overflow-y-auto">
                {buckets[s].length === 0 && <div className="text-[10px] italic text-muted-foreground">—</div>}
                {buckets[s].map((c) => (
                  <div key={c.id} className="border border-border p-1.5 text-[11px]" data-testid={`growth-campaign-${c.id}`}>
                    <div className="font-medium truncate">{c.name}</div>
                    {c.goal && <div className="text-[10px] text-muted-foreground truncate">{c.goal}</div>}
                    <div className="text-[9px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      {c.start_date && <span>{c.start_date}</span>}
                      {c.budget && <span>· ₹{c.budget}</span>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px]" onClick={() => onEdit(c)} data-testid={`growth-campaign-edit-${c.id}`}><Edit3 className="w-2.5 h-2.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px] text-destructive" onClick={() => onDelete(c)} data-testid={`growth-campaign-delete-${c.id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function OutreachSection({ items, canEdit, onNew, onEdit, onDelete }) {
  const buckets = useMemo(() => {
    const b = { to_reach: [], contacted: [], responded: [], declined: [], won: [] };
    (items || []).forEach((o) => { (b[o.status] || b.to_reach).push(o); });
    return b;
  }, [items]);
  return (
    <section className="border border-border" data-testid="growth-cockpit-outreach">
      <SectionHead
        icon={PhoneCall} title="Agency outreach" count={(items || []).length}
        sub="prospects, VC intros, PR contacts, hiring targets"
        action={canEdit && <Button size="sm" className="rounded-none h-7" onClick={onNew} data-testid="growth-cockpit-outreach-new"><Plus className="w-3 h-3 mr-1" /> New target</Button>}
      />
      {(items || []).length === 0 ? (
        <div className="p-3 text-xs italic text-muted-foreground">Track your outbound as a pipeline — VC intros, PR journalists, hiring candidates.</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-5 p-3">
          {["to_reach", "contacted", "responded", "declined", "won"].map((s) => (
            <div key={s} className="border border-border" data-testid={`growth-outreach-${s}`}>
              <div className={`px-2 py-1.5 border-b border-border text-[10px] uppercase tracking-widest font-semibold ${OUTREACH_STATUS_TONE[s]}`}>{s.replace(/_/g, " ")} · {buckets[s].length}</div>
              <div className="p-2 space-y-1.5 max-h-60 overflow-y-auto">
                {buckets[s].length === 0 && <div className="text-[10px] italic text-muted-foreground">—</div>}
                {buckets[s].map((o) => (
                  <div key={o.id} className="border border-border p-1.5 text-[11px]" data-testid={`growth-outreach-item-${o.id}`}>
                    <div className="font-medium truncate">{o.name}</div>
                    {o.org && <div className="text-[10px] text-muted-foreground truncate">{o.org}</div>}
                    <div className="text-[9px] text-muted-foreground truncate">{o.channel}{o.next_step ? ` · ${o.next_step}` : ""}</div>
                    {canEdit && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px]" onClick={() => onEdit(o)} data-testid={`growth-outreach-edit-${o.id}`}><Edit3 className="w-2.5 h-2.5" /></Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-5 px-1 text-[9px] text-destructive" onClick={() => onDelete(o)} data-testid={`growth-outreach-delete-${o.id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SectionHead({ icon: Icon, title, count, sub, action }) {
  return (
    <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-primary" />
      <span className="font-semibold uppercase tracking-widest">{title}</span>
      {typeof count === "number" && <span className="normal-case text-[10px] text-muted-foreground">· {count}</span>}
      {sub && <span className="normal-case text-[10px] text-muted-foreground italic">— {sub}</span>}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

// ─────────── Editors ───────────

function CampaignEditor({ wsId, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    goal: initial?.goal || "",
    status: initial?.status || "draft",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    budget: initial?.budget || "",
    channels: (initial?.channels || []).join(", "),
    tags: (initial?.tags || []).join(", "),
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...(isNew ? { scope: initial.scope } : {}),
      name: form.name, description: form.description, goal: form.goal,
      status: form.status,
      start_date: form.start_date || null, end_date: form.end_date || null,
      budget: form.budget ? Number(form.budget) : null,
      channels: form.channels.split(",").map((c) => c.trim()).filter(Boolean),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      notes: form.notes,
    };
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/campaigns`, payload);
      else await api.patch(`/campaigns/${initial.id}`, payload);
      toast.success(isNew ? "Campaign created" : "Campaign updated");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="growth-cockpit-campaign-editor">
        <DialogHeader><DialogTitle>{isNew ? "New agency campaign" : "Edit campaign"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="growth-campaign-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Goal</Label>
            <Input value={form.goal} onChange={(e) => set("goal", e.target.value)} placeholder="10 new clients · 5 hires · 1000 followers" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["draft", "live", "paused", "done"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Budget (₹)</Label>
            <Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Start</Label>
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">End</Label>
            <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Channels (comma)</Label>
            <Input value={form.channels} onChange={(e) => set("channels", e.target.value)} placeholder="linkedin, email, referrals" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Tags (comma)</Label>
            <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="growth-campaign-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutreachEditor({ wsId, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "", org: initial?.org || "",
    channel: initial?.channel || "email", contact: initial?.contact || "",
    url: initial?.url || "", status: initial?.status || "to_reach",
    next_step: initial?.next_step || "", next_step_date: initial?.next_step_date || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...(isNew ? { scope: initial.scope } : {}), ...form };
    setBusy(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/outreach-targets`, payload);
      else await api.patch(`/outreach-targets/${initial.id}`, payload);
      toast.success(isNew ? "Target added" : "Target updated");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-none" data-testid="growth-cockpit-outreach-editor">
        <DialogHeader><DialogTitle>{isNew ? "New agency outreach" : "Edit target"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="growth-outreach-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Org</Label>
            <Input value={form.org} onChange={(e) => set("org", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Channel</Label>
            <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["email", "linkedin", "twitter", "instagram", "phone", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Contact (email/handle)</Label>
            <Input value={form.contact} onChange={(e) => set("contact", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["to_reach", "contacted", "responded", "declined", "won"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Next step date</Label>
            <Input type="date" value={form.next_step_date} onChange={(e) => set("next_step_date", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Next step</Label>
            <Input value={form.next_step} onChange={(e) => set("next_step", e.target.value)} placeholder="Book intro call" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="growth-outreach-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelsEditor({ wsId, workspace, onClose, onSaved }) {
  const [rows, setRows] = useState(
    workspace.marketing_channels?.length
      ? workspace.marketing_channels
      : [{ platform: "linkedin", handle: "", url: "", followers_manual: 0 }],
  );
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const add = () => setRows((p) => [...p, { platform: "linkedin", handle: "", url: "", followers_manual: 0 }]);
  const rm = (i) => setRows((p) => p.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      const payload = rows.map((r) => ({
        platform: r.platform, handle: r.handle || null, url: r.url || null,
        followers_manual: r.followers_manual != null && r.followers_manual !== "" ? Number(r.followers_manual) : null,
        last_post_at: r.last_post_at || null,
      }));
      await api.patch(`/workspaces/${wsId}/channels`, { channels: payload });
      toast.success("Channels saved");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="growth-cockpit-channels-editor">
        <DialogHeader><DialogTitle>Agency social channels</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border p-2" data-testid={`growth-channel-row-${i}`}>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">Platform</Label>
                <Select value={r.platform} onValueChange={(v) => upd(i, "platform", v)}>
                  <SelectTrigger className="h-8 rounded-none text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(PLATFORM_ICONS).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">Handle</Label>
                <Input value={r.handle || ""} onChange={(e) => upd(i, "handle", e.target.value)} className="h-8 rounded-none text-xs" placeholder="@agency" />
              </div>
              <div className="col-span-3">
                <Label className="text-[9px] uppercase tracking-widest">URL</Label>
                <Input value={r.url || ""} onChange={(e) => upd(i, "url", e.target.value)} className="h-8 rounded-none text-xs" />
              </div>
              <div className="col-span-2">
                <Label className="text-[9px] uppercase tracking-widest">Followers</Label>
                <Input type="number" value={r.followers_manual ?? ""} onChange={(e) => upd(i, "followers_manual", e.target.value)} className="h-8 rounded-none text-xs" />
              </div>
              <div className="col-span-1 flex items-end">
                <Button size="sm" variant="ghost" className="rounded-none h-8 w-full text-destructive" onClick={() => rm(i)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="rounded-none" onClick={add} data-testid="growth-channels-add">
            <Plus className="w-3 h-3 mr-1" /> Add channel
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="growth-channels-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
